import { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Camera,
  CheckCircle2,
  AlertCircle,
  Clock,
  Eye,
  RotateCcw,
  Trash2
} from "lucide-react";
import { cn } from "@/lib/utils";
import { createThumbnail } from "@/lib/photoUtils";

interface PhotoStep {
  id: string;
  title: string;
  description: string;
  instruction: string;
  example: string;
  required?: boolean;
  category?: 'frontal' | 'profile' | 'occlusal' | 'buccal';
}

interface ModernPhotoGridProps {
  steps: PhotoStep[];
  capturedPhotos: Record<string, string>;
  currentStepIndex: number;
  onStepSelect: (index: number) => void;
  onPhotoView: (stepId: string) => void;
  onPhotoRetake: (stepId: string) => void;
  onPhotoDelete?: (stepId: string) => void;
  showActions?: boolean;
  className?: string;
}

export function ModernPhotoGrid({
  steps,
  capturedPhotos,
  currentStepIndex,
  onStepSelect,
  onPhotoView,
  onPhotoRetake,
  onPhotoDelete,
  showActions = true,
  className
}: ModernPhotoGridProps) {
  const [thumbnails, setThumbnails] = useState<Record<string, string>>({});
  const [loadingThumbnails, setLoadingThumbnails] = useState<Set<string>>(new Set());

  // Generate thumbnail for photo
  const generateThumbnail = useCallback(async (stepId: string, imageData: string) => {
    if (thumbnails[stepId] || loadingThumbnails.has(stepId)) return;
    
    if (!imageData || typeof imageData !== 'string' || imageData.length === 0) {
      console.warn('Invalid image data for thumbnail generation:', stepId);
      return;
    }

    setLoadingThumbnails(prev => new Set(prev).add(stepId));
    
    try {
      const thumbnail = await createThumbnail(imageData, 200);
      setThumbnails(prev => ({ ...prev, [stepId]: thumbnail }));
    } catch (error) {
      console.error('Failed to generate thumbnail for', stepId, ':', error);
    } finally {
      setLoadingThumbnails(prev => {
        const newSet = new Set(prev);
        newSet.delete(stepId);
        return newSet;
      });
    }
  }, [thumbnails, loadingThumbnails]);

  // Get photo stats
  const getPhotoStats = useCallback((imageData: string) => {
    if (!imageData || typeof imageData !== 'string') {
      return {
        size: '0KB',
        timestamp: Date.now()
      };
    }
    
    // Only calculate size for base64 data URLs, not for HTTP/blob URLs
    let size = 0;
    if (imageData.startsWith('data:')) {
      const base64 = imageData.split(",")[1] ?? "";
      const padding = (base64.match(/=+$/)?.[0]?.length ?? 0);
      size = Math.max(0, (base64.length * 3) / 4 - padding);
    }
    
    return {
      size: size > 0 
        ? (size > 1024 * 1024 ? `${(size / (1024 * 1024)).toFixed(1)}MB` : `${(size / 1024).toFixed(0)}KB`)
        : 'Unknown',
      timestamp: Date.now() // This would come from actual capture time
    };
  }, []);

  // Get category color
  const getCategoryColor = (category?: string) => {
    switch (category) {
      case 'frontal': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'profile': return 'bg-green-100 text-green-800 border-green-200';
      case 'occlusal': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'buccal': return 'bg-orange-100 text-orange-800 border-orange-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Get completion stats
  const completionStats = {
    total: steps.length,
    completed: Object.keys(capturedPhotos).length,
    required: steps.filter(s => s.required !== false).length,
    requiredCompleted: steps.filter(s => s.required !== false && capturedPhotos[s.id]).length
  };

  return (
    <div className={cn("space-y-6", className)}>
      {/* Progress Summary */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold text-gray-900">Photo Progress</h3>
          <Badge variant="outline" className="bg-white">
            {completionStats.completed}/{completionStats.total} Complete
          </Badge>
        </div>
        
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">{completionStats.completed}</div>
            <div className="text-gray-600">Captured</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">{completionStats.requiredCompleted}</div>
            <div className="text-gray-600">Required</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-600">{completionStats.total - completionStats.completed}</div>
            <div className="text-gray-600">Remaining</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-600">
              {Math.round((completionStats.completed / completionStats.total) * 100)}%
            </div>
            <div className="text-gray-600">Progress</div>
          </div>
        </div>
      </div>

      {/* Enhanced Photo Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-3 gap-3 sm:gap-4">
        {steps.map((step, index) => {
          const isCompleted = !!capturedPhotos[step.id];
          const isCurrent = index === currentStepIndex;
          const imageData = capturedPhotos[step.id];
          const stats = imageData && typeof imageData === 'string' && imageData.length > 0 ? getPhotoStats(imageData) : null;

          // Generate thumbnail if needed (only for data URLs, not for HTTP/blob URLs)
          if (imageData && typeof imageData === 'string' && imageData.length > 0 && 
              imageData.startsWith('data:') && !thumbnails[step.id] && !loadingThumbnails.has(step.id)) {
            generateThumbnail(step.id, imageData);
          }

          const categoryColors: Record<NonNullable<PhotoStep["category"]>, string> = {
            frontal: 'border-blue-200 bg-blue-50',
            profile: 'border-green-200 bg-green-50',
            occlusal: 'border-purple-200 bg-purple-50',
            buccal: 'border-orange-200 bg-orange-50'
          };
          const categoryClass = step.category ? categoryColors[step.category] : undefined;

          return (
            <div
              key={step.id}
              className={cn(
                "relative group cursor-pointer transition-all duration-200",
                "border-2 rounded-xl overflow-hidden",
                "hover:shadow-lg hover:scale-105",
                isCurrent && "ring-2 ring-jema-blue ring-offset-2 scale-105",
                isCompleted ? "border-green-300 bg-green-50" : categoryClass || "border-gray-200 bg-gray-50"
              )}
              onClick={() => {
                if (isCompleted) {
                  onPhotoView(step.id);
                } else {
                  onStepSelect(index);
                }
              }}
            >
              {/* Step Number Badge */}
              <div className={cn(
                "absolute top-2 left-2 z-10 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shadow-sm",
                isCompleted ? "bg-green-500 text-white" : 
                isCurrent ? "bg-jema-blue text-white" : "bg-gray-400 text-white"
              )}>
                {isCompleted ? '✓' : index + 1}
              </div>

              {/* Category Badge */}
              {step.category && (
                <div className="absolute top-2 right-2 z-10">
                  <Badge 
                    variant="outline" 
                    className={cn("text-xs shadow-sm bg-white/90", getCategoryColor(step.category))}
                  >
                    {step.category}
                  </Badge>
                </div>
              )}

              {/* Photo Area */}
              <div className="aspect-square relative">
                {isCompleted ? (
                  <>
                    <img 
                      src={thumbnails[step.id] || imageData} 
                      alt={step.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        console.warn('Failed to load image for step:', step.id);
                        // Hide the broken image
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                    
                    {/* Success Overlay */}
                    <div className="absolute inset-0 bg-green-500/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="bg-white/90 rounded-full p-3">
                        <CheckCircle2 className="w-8 h-8 text-green-600" />
                      </div>
                    </div>

                    {/* Photo Stats Overlay */}
                    {stats && showActions && (
                      <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-xs p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="flex justify-between items-center">
                            <span>{stats.size}</span>
                          <Clock className="w-3 h-3" />
                        </div>
                      </div>
                    )}

                    {/* Action Buttons Overlay */}
                    {showActions && (
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        <Button
                          size="sm"
                          variant="secondary"
                          className="bg-white/90 hover:bg-white"
                          onClick={(e) => {
                            e.stopPropagation();
                            onPhotoView(step.id);
                          }}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="secondary"
                          className="bg-white/90 hover:bg-white"
                          onClick={(e) => {
                            e.stopPropagation();
                            onPhotoRetake(step.id);
                          }}
                        >
                          <RotateCcw className="w-4 h-4" />
                        </Button>
                        {onPhotoDelete && (
                          <Button
                            size="sm"
                            variant="secondary"
                            className="bg-red-100/90 hover:bg-red-100 text-red-600"
                            onClick={(e) => {
                              e.stopPropagation();
                              onPhotoDelete(step.id);
                            }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center">
                    <Camera className={cn(
                      "w-10 h-10 mb-2",
                      isCurrent ? "text-jema-blue" : "text-gray-400"
                    )} />
                    {isCurrent && (
                      <div className="text-xs font-medium text-jema-blue animate-pulse">
                        Current
                      </div>
                    )}
                    {step.required !== false && !isCompleted && (
                      <AlertCircle className="w-4 h-4 text-orange-500 mt-1" />
                    )}
                  </div>
                )}
              </div>

              {/* Step Info */}
              <div className="p-3">
                <h4 className="font-medium text-sm leading-tight text-center text-gray-800 mb-1">
                  {step.title}
                </h4>
                <p className="text-xs text-gray-600 text-center line-clamp-2 mb-2">
                  {step.description}
                </p>
                
                {/* Step Badges */}
                <div className="flex items-center justify-center gap-1">
                  <Badge variant="outline" className="text-xs">
                    {index + 1}
                  </Badge>
                  {step.required !== false && !isCompleted && (
                    <Badge variant="outline" className="text-xs text-orange-600 border-orange-200">
                      Required
                    </Badge>
                  )}
                </div>
              </div>

              {/* Current Step Indicator */}
              {isCurrent && !isCompleted && (
                <div className="absolute inset-0 border-2 border-jema-blue rounded-xl animate-pulse" />
              )}
            </div>
          );
        })}
      </div>

      {/* Quick Actions */}
      {showActions && completionStats.completed > 0 && (
        <div className="flex flex-wrap gap-2 justify-center pt-4 border-t">
          {completionStats.completed < completionStats.total && (
            <Button variant="outline" size="sm" className="text-orange-600 border-orange-200">
              <AlertCircle className="w-4 h-4 mr-2" />
              {completionStats.total - completionStats.completed} Photos Needed
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
